import classNames from 'classnames';
import moment from 'moment';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { DefaultRootState, useDispatch, useSelector } from 'react-redux';

import { updateCheckedItems } from 'actions/videosDashboard';
import { CardVariant } from 'constants/destinationLogging';
import { VIDEO_CARD_CLICKED } from 'constants/events';
import { V4 } from 'constants/recordingVersions';
import { LOOM_URI } from 'constants/routes';
import { VIDEO } from 'constants/videosDashboard';
import { selectCheckedItems } from 'selectors/videoDashboard';
import { track } from 'utilities/analytics';
import { ContextType, Item } from 'utilities/menuOptions/types';
import { shorthandTimestampMomentConfig } from 'utilities/time';

import {
  getSurfaceName,
  SectionTitleContext,
} from 'contexts/SectionTitleContext';

import Body from './Body';
import Thumbnail from './Thumbnail';
import { VideoCardVideo } from './types';
import { CARD_CAPABILITIES } from '../../constants/videoCard';

import styles from './styles.module.less';

export default function VideoCard({
  video,
  sort = 0,
  currentPinMaxValue = 0,
  context,
  showPostVideoFTUX = false,
  onCheck = () => {
    /* noop */
  },
}: {
  video: VideoCardVideo;
  sort: number;
  currentPinMaxValue: number;
  context: ContextType;
  showPostVideoFTUX: boolean;
  onCheck: (isChecked, isMultiSelect) => void;
}): JSX.Element {
  const dispatch = useDispatch();
  const sectionTitle = useContext(SectionTitleContext);

  const checkedItems = useSelector<DefaultRootState, Item[]>(
    selectCheckedItems
  );

  const renameId = useSelector(
    state => (state as { videos_dashboard }).videos_dashboard.rename_id
  );
  const beingRenamed = renameId === video.id;

  const [hasActiveHoverState, setHasActiveHoverState] = useState(false);
  const [isChecked, setIsChecked] = useState(
    Boolean(
      checkedItems.find(item => item.type === VIDEO && item.id === video.id)
    )
  );

  sort = sort === 0 && video.profileSort ? video.profileSort : sort;

  video = useMemo(
    () => ({
      ...video,
      isAnimated: video.use_gif,
      isUserUploadedVideo: video.video_properties.recording_version === V4,
      videoUploadValid: video.processing_information.videoUploadValid,
      isPinned: sort > 0,
    }),
    [video, sort]
  );

  useEffect(() => {
    setIsChecked(
      Boolean(
        checkedItems.find(item => item.type === VIDEO && item.id === video.id)
      )
    );
  }, [checkedItems, video.id]);

  const videoUploadFailed =
    video.isUserUploadedVideo && video.videoUploadValid === false;

  const videoUrl = `${LOOM_URI}/share/${video?.id}`;

  const linkRef = useRef<HTMLAnchorElement>(null);

  // Drag and drop actions
  const [, dragRef, connectDragPreview] = useDrag({
    // useDrag needs a type in item in order to work
    item: { ...video, type: VIDEO },
    begin() {
      if (!isChecked) {
        onCheck(true, false);
      }
    },
    end() {
      const checkedItemWithoutDraggedVideo = checkedItems.filter(
        item => item.id !== video.id
      );

      if (checkedItemWithoutDraggedVideo.length === 0) {
        dispatch(updateCheckedItems([]));
      }
    },
    canDrag: () =>
      !beingRenamed && CARD_CAPABILITIES['canDrag'].includes(context),

    collect: monitor => ({
      isDragging: Boolean(monitor.isDragging()),
    }),
  });

  useEffect(() => {
    connectDragPreview(getEmptyImage(), { captureDraggingState: true });
  }, [connectDragPreview]);

  const hasTags = video?.tags.length > 0;

  moment.locale('en', shorthandTimestampMomentConfig());

  const LinkWrapper = useCallback(() => {
    if (beingRenamed || videoUploadFailed) {
      return <span className={styles.videoCardLink} />;
    }

    if (checkedItems.length > 0) {
      return (
        <a
          className={styles.videoCardLink}
          href="#"
          onClick={e => {
            e.preventDefault();
            onCheck(!isChecked, e.shiftKey);
          }}
        ></a>
      );
    }

    return (
      <a
        className={styles.videoCardLink}
        href={videoUrl}
        ref={linkRef}
        // If this has a video, play the animated preview on hover
        onMouseOver={e =>
          (e.currentTarget.nextSibling as HTMLElement)
            ?.querySelector('video')
            ?.play()
        }
        onMouseOut={e => {
          (e.currentTarget.nextSibling as HTMLElement)
            ?.querySelector('video')
            ?.pause();
          linkRef.current?.classList.remove(styles.showHoverState);
        }}
        onClick={() => {
          if (!sectionTitle) {
            return;
          }

          track(VIDEO_CARD_CLICKED, {
            video_id: video.id,
            card_variant: CardVariant.LINK_TO_SHARE_PAGE,
            links_to: videoUrl,
            surface: getSurfaceName(sectionTitle),
          });
        }}
      >
        <h2 className="srOnly">{video?.name}</h2>
      </a>
    );
    // Only these dependencies should be specified to avoid issues with drag since checked items are changed when the drag starts
  }, [beingRenamed, video?.name, videoUploadFailed, videoUrl]);

  return (
    <article
      ref={dragRef}
      data-videoid={video.id}
      className={classNames(
        styles.videoCard,
        hasTags && styles.hasTags,
        checkedItems.length && styles.activeCheckState,
        hasActiveHoverState && styles.activeHoverState
      )}
    >
      <LinkWrapper />
      <div className="overflow:hidden radius:medium relative">
        <Thumbnail
          video={video}
          context={context}
          isChecked={isChecked}
          linkRef={linkRef}
          sort={sort}
          currentPinMaxValue={currentPinMaxValue}
          onCheck={onCheck}
        />
        <Body
          video={video}
          context={context}
          linkRef={linkRef}
          showPostVideoFTUX={showPostVideoFTUX}
          setHasActiveHoverState={setHasActiveHoverState}
        />
      </div>
    </article>
  );
}
